// Money is always integer minor units (cents). Never floats — a membership
// billed at $49.99 x 12 must not drift, and proration divides constantly.

export const ZERO = 0;

export function money(major, minor = 0) {
  return Math.round(major * 100) + minor;
}

export function add(...amounts) {
  return amounts.reduce((a, b) => a + b, 0);
}

export function subtract(a, b) {
  return a - b;
}

export function multiply(amount, factor) {
  return Math.round(amount * factor);
}

/**
 * Split an amount into n parts that sum exactly to the original.
 * Remainder cents go to the earliest parts, so 100 / 3 => [34, 33, 33].
 * Used for installment plans and for splitting a family invoice.
 */
export function allocate(amount, n) {
  if (n <= 0) throw new Error('allocate: n must be positive');
  const sign = amount < 0 ? -1 : 1;
  const abs = Math.abs(amount);
  const base = Math.floor(abs / n);
  const remainder = abs - base * n;
  return Array.from({ length: n }, (_, i) => sign * (base + (i < remainder ? 1 : 0)));
}

/**
 * Allocate by ratio, preserving the total exactly. Used for proration and for
 * splitting a discount across invoice lines.
 */
export function allocateByRatio(amount, ratios) {
  const total = ratios.reduce((a, b) => a + b, 0);
  if (total === 0) return ratios.map(() => 0);
  const raw = ratios.map((r) => (amount * r) / total);
  const floored = raw.map((v) => Math.floor(v));
  let remainder = amount - floored.reduce((a, b) => a + b, 0);
  // Hand out leftover cents to the largest fractional parts first.
  const order = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);
  const out = floored.slice();
  for (let k = 0; remainder > 0; k++, remainder--) {
    out[order[k % order.length].i] += 1;
  }
  return out;
}

const CURRENCY_MINOR_DIGITS = { USD: 2, EUR: 2, GBP: 2, AUD: 2, CAD: 2, JPY: 0 };

export function format(amount, currency = 'USD', locale = 'en-US') {
  const digits = CURRENCY_MINOR_DIGITS[currency] ?? 2;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
  }).format(amount / 10 ** digits);
}
